import * as React from 'react';
import { Controller, useFormContext, useFormState } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { Close } from '@mui/icons-material';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getNestedFormErrors } from 'src/shared/functions/getNestedFormErrors';

type ControlledAutocompleteDropdownLazyProps<T> = {
  nameProp: string;
  label: string;
  name: string;
  fetchMethod: () => Promise<T[]>;
  callback: (selected: any[] | undefined) => void;
  componentWidth?: string;
  placeholder?: string;
  renderOption?: (option: any) => string;
  valueProp?: string;
};

export const ControlledAutocompleteDropdownLazy = <T,>(props: ControlledAutocompleteDropdownLazyProps<T>) => {
  const {
    nameProp,
    label,
    name,
    fetchMethod,
    callback,
    componentWidth = '15rem',
    placeholder = undefined,
    renderOption = undefined,
    valueProp = undefined,
  } = props;
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<readonly T[]>([]);
  const loading = open && options.length === 0;

  const isMounted = useIsMounted();

  const { control } = useFormContext();
  const { errors } = useFormState();

  React.useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    if (!options.length) {
      (async () => {
        const result = await fetchMethod();

        if (active && isMounted()) {
          setOptions([...result]);
        }
      })();
    }

    return () => {
      active = false;
    };
  }, [fetchMethod, isMounted, loading, options.length]);

  // React.useEffect(() => {
  //   if (!open && isMounted()) {
  //     setOptions([]);
  //   }
  // }, [open, isMounted]);

  const componentId = `autocomplete-${Date.now().toString(36) + Math.random().toString(36).substring(2)}`;

  let isError = false;
  const errorMessage = getNestedFormErrors(errors, name ?? '');
  if (errorMessage) {
    isError = true;
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Autocomplete
          id={componentId}
          options={options}
          autoHighlight
          open={open}
          openOnFocus
          // disableCloseOnSelect
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          loading={loading}
          getOptionLabel={(option) => option[nameProp]}
          renderOption={renderOption || undefined}
          onChange={(event: any, newValue: any) => {
            field.onChange(newValue);
            callback(newValue);
          }}
          value={field.value || null}
          isOptionEqualToValue={(option, val) => {
            return option === val;
          }}
          // inputValue={field.value}
          // onInputChange={(event, newInputValue) => {
          //    setInputValue(newInputValue);
          // }}
          renderInput={(params) => (
            <TextField
              {...params}
              size='small'
              sx={{ width: componentWidth }}
              label={label}
              placeholder={placeholder}
              {...field}
              error={isError}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                ...params.inputProps,
                autoComplete: 'off', // disable autocomplete and autofill. new-password Not working in current chrome, off seems to work.
              }}
              helperText={isError && errorMessage ? <em>{errorMessage}</em> : null}
            />
          )}
          clearIcon={<Close fontSize='small' />}
          sx={{
            '& .MuiChip-sizeMedium ': { height: 'auto', borderRadius: 1 },
            '& .MuiChip-deleteIcon ': { fontSize: '18px' },
          }}
        />
      )}
    />
  );
};
